import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type PendingArchive = {
  id: string;
  workspace_id: string;
  user_id: string;
  archive_at: string;
  reason: string;
  created_at: string;
  archived_at: string | null;
};

/**
 * Workspaces the current user has flagged for hard-deletion at period end.
 * Cached via TanStack Query so the workspace tabs, banners, and add-on
 * picker all share one network request that survives navigation.
 */
export function usePendingArchives() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  const queryKey = useMemo(() => ["pending_workspace_archives", userId] as const, [userId]);

  const { data: archives = [], isLoading, refetch } = useQuery({
    queryKey,
    enabled: !!userId,
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    queryFn: async (): Promise<PendingArchive[]> => {
      const { data, error } = await supabase
        .from("pending_workspace_archives")
        .select("*")
        .eq("user_id", userId!)
        .is("archived_at", null);
      if (error) throw error;
      return (data ?? []) as PendingArchive[];
    },
  });

  useEffect(() => {
    if (!userId) return;
    const onFocus = () => qc.invalidateQueries({ queryKey });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userId, qc, queryKey]);

  const pendingIds = new Set(archives.map((a) => a.workspace_id));

  return {
    archives,
    pendingIds,
    loading: !!userId && isLoading,
    refresh: async () => { await refetch(); },
  };
}
